import {
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useAtomValue } from 'jotai';
import moment from 'moment';
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useStats } from '../hooks/useStats';
import {
  loadingTeamMembersJotaiAtom,
  teamMemberIdsJotaiAtom,
  teamMembersJotaiAtom,
  teamsJotaiAtom
} from '../jotai/atoms';
import { dummyMemberJa } from '../utils/dummy';
import type { Params } from './Dashboard';
import { UserProfile } from './UserProfile';

/**
 * ダッシュボードの「メンバー」タブ
 */
export function DashboardMembers() {
  // これ以上特典を利用できるか確認する
  const { teamId } = useParams<Params>();
  const { canIncrease, over } = useStats(teamId);

  // 現在のチームメンバーの ID
  const memberIds = useAtomValue(teamMemberIdsJotaiAtom(teamId));

  const enabled = useAtomValue(teamsJotaiAtom(teamId))?.enabled;

  const loading = useAtomValue(loadingTeamMembersJotaiAtom(teamId));

  return (
    <>
      {over ? (
        <Alert severity="warning">
          有効人数の上限を超えています。課金が発生する前に、人数を調整してください
        </Alert>
      ) : null}
      {enabled === false ? (
        <Alert severity="info">
          こちらではメンバーを管理できます。アカウントの有効化後にご利用いただけます。
        </Alert>
      ) : null}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>メンバー</TableCell>
            <TableCell>名前</TableCell>
            <TableCell>今見ているページ</TableCell>
            <TableCell>最終更新</TableCell>
            <TableCell>タグ</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {enabled === false ? (
            <MemberRow
              teamId={teamId}
              uid={dummyMemberJa().uid}
              canIncrease={false}
            />
          ) : null}
          {memberIds.map(id => (
            <MemberRow
              key={id}
              teamId={teamId}
              uid={id}
              canIncrease={canIncrease}
            />
          ))}
        </TableBody>
      </Table>
      {loading ? (
        <Typography variant="h6">メンバーを取得しています</Typography>
      ) : null}
    </>
  );
}

const styles = {
  row: {
    cursor: 'pointer'
  },
  currentPage: {
    '& > *': {
      maxWidth: '16em',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      wordBreak: 'break-all',
      textOverflow: 'ellipsis',
      display: 'block'
    },
    transition: 'opacity 200ms'
  },
  old: {
    opacity: 0.9
  },
  older: {
    opacity: 0.1
  },
  noRights: {
    backgroundColor: 'rgba(0,0,0,0.1)'
  }
} as const;

interface MemberRowProps {
  teamId: string;
  uid: string;
  canIncrease: boolean;
}

function MemberRow({ teamId, uid }: MemberRowProps) {
  // メンバー詳細を開く
  const history = useHistory();
  const handleOpen = React.useCallback(() => {
    if (uid === dummyMemberJa().uid) return; // ダミーデータの詳細はない
    history.push(`/${teamId}/members/${uid}`);
  }, [teamId, uid]);

  const member = useAtomValue(teamMembersJotaiAtom({ teamId, uid }));
  // console.warn(uid, member);
  const millis = member?.currentPage?.updatedAt?.toMillis();
  const isOld = millis && Date.now() - millis > 60 * 1000; // １分経ったら少し薄くする
  const isOlder = millis && Date.now() - millis > 600 * 1000; // １０分経ったら極限まで薄くする
  const [value, setValue] = React.useState(0);
  React.useEffect(() => {
    // ３０秒おきに更新する
    const id = window.setTimeout(() => setValue(value + 1), 30 * 1000);
    return () => window.clearTimeout(id);
  }, [value]);
  const fromNow = (millis && moment(millis).fromNow()) || '';

  return member ? (
    <TableRow
      hover
      sx={[styles.row, !member.rights ? styles.noRights : {}]}
      onClick={handleOpen}
      data-testid="member-row"
    >
      <TableCell>
        <UserProfile uid={member.uid} />
      </TableCell>
      <TableCell>{member.name}</TableCell>
      <TableCell
        sx={[
          styles.currentPage,
          isOld ? styles.old : {},
          isOlder ? styles.older : {}
        ]}
      >
        {member.disableCurrentPage ? (
          <Typography variant="body2" color="text.secondary">
            表示できない設定になっています
          </Typography>
        ) : member.currentPage ? (
          <>
            <a
              href={`https://www.hackforplay.xyz/${member.currentPage.uri}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              title={member.currentPage.title}
            >
              {member.currentPage.title}
            </a>
            <Typography variant="body2" color="text.secondary">
              {member.currentPage.status}
            </Typography>
          </>
        ) : (
          ''
        )}
      </TableCell>
      <TableCell>{member.disableCurrentPage ? '' : fromNow}</TableCell>
      <TableCell>
        {[...(member.tags || [])].map(tag => (
          <Chip key={tag} size="small" label={tag} />
        ))}
      </TableCell>
    </TableRow>
  ) : null;
}
